import { db } from "@/_firebase/firebaseConfig";
import { useAuth } from "@/context/AuthContext";
import {
  Item,
  ItemResponse,
  ScheduledAssessment,
  TrackingFrequency,
} from "@dimensional-engineering/dimensional-models";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { useEffect, useState } from "react";

export const traitAssessmentTitleMap: Record<number, string> = {
  0: "Check-in",
  1: "Check-in",
  2: "Check-in",
};

export const trackingFrequencyMap: Record<TrackingFrequency, string> = {
  annually: "Once per year",
  biannually: "Twice per year",
  quarterly: "Every 3 months",
};

export function useGetTraitTraitAssessmentItems(
  type: "element" | "pattern",
  slug: string
) {
  const [items, setItems] = useState<Item[]>([]);

  useEffect(() => {
    const q = query(
      collection(db, "/items"),
      where("traitAssessmentIdentifiers", "array-contains", { slug, type })
    );

    async function getItems() {
      const tempItems: Item[] = [];
      await getDocs(q).then((res) => {
        res.forEach((d) => {
          const data = d.data() as Item;
          tempItems.push(data);
        });
      });
      const sorted = tempItems.sort((a, b) => {
        const orderA =
          a.traitAssessmentItemMappings?.find(
            (t) => t.traitIdentifier.slug === slug
          )?.order ?? 0;
        const orderB =
          b.traitAssessmentItemMappings?.find(
            (t) => t.traitIdentifier.slug === slug
          )?.order ?? 0;
        return orderA - orderB;
      });
      setItems(sorted);
    }

    getItems();
  }, [type, slug]);

  return items;
}

export function useGetTraitTraitAssessmentItemResponses(props: {
  scheduledAssessment: ScheduledAssessment;
}) {
  const { user } = useAuth();

  const [responses, setResponses] = useState<ItemResponse[]>([]);

  useEffect(() => {
    async function getResponses() {
      const traitAssessmentDocRef = collection(
        db,
        `members/${user?.uid}/traitAssessmentItemResponses/${props.scheduledAssessment.scheduleIdentifier}/responses/`
      );
      const tempResponses: ItemResponse[] = [];
      await getDocs(traitAssessmentDocRef).then((res) => {
        res.forEach((d) => {
          const data = d.data() as ItemResponse;
          tempResponses.push(data);
        });
      });
      setResponses(tempResponses);
    }
    getResponses();
  }, [props.scheduledAssessment.scheduleIdentifier]);

  return responses;
}

export async function setResponseCollectionDocument(
  scheduledAssessment: ScheduledAssessment,
  uid: string | undefined
) {
  const traitAssessmentDocRef = doc(
    db,
    `/members/${uid}/traitAssessmentItemResponses/${scheduledAssessment.scheduleIdentifier}`
  );

  await setDoc(traitAssessmentDocRef, {
    scheduleIdentifier: scheduledAssessment.scheduleIdentifier,
  });
}

export async function saveTraitAssessmentResponse(
  response: ItemResponse,
  scheduledAssessment: ScheduledAssessment,
  uid: string | undefined
) {
  const traitAssessmentDocRef = doc(
    db,
    `members/${uid}/traitAssessmentItemResponses/${scheduledAssessment.scheduleIdentifier}/responses/${response.itemId}`
  );

  await setDoc(traitAssessmentDocRef, response);
}

export async function deleteResponseCollectionDocument(
  scheduledAssessment: ScheduledAssessment,
  uid: string | undefined
) {
  const traitAssessmentDocRef = doc(
    db,
    `/members/${uid}/traitAssessmentItemResponses/${scheduledAssessment.scheduleIdentifier}`
  );

  const responsesRef = collection(traitAssessmentDocRef, "responses");
  const responsesDocs = await getDocs(responsesRef);

  for (const responseDoc of responsesDocs.docs) {
    await deleteDoc(responseDoc.ref);
  }

  await deleteDoc(traitAssessmentDocRef);
}

export function getMissingItems(
  items: Item[],
  itemResponses: ItemResponse[]
): Item[] {
  const responseIds = new Set(itemResponses.map((res) => res.itemId));
  return items.filter((item) => !responseIds.has(item.slug));
}

export function removeDuplicateItemResponses(
  itemResponses: ItemResponse[]
): ItemResponse[] {
  const seen = new Set<string>();
  return itemResponses.filter((response) => {
    if (seen.has(response.itemId)) {
      return false;
    }
    seen.add(response.itemId);
    return true;
  });
}
